

import React from 'react';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';

interface StatCardWidgetProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  icon?: React.ReactNode;
  color?: 'gold' | 'blue' | 'green' | 'red' | 'purple' | 'orange';
  onClick?: () => void;
  loading?: boolean;
}

const colorClasses = {
  gold: 'bg-amber-50 border-amber-200 text-amber-900',
  blue: 'bg-blue-50 border-blue-200 text-blue-900',
  green: 'bg-green-50 border-green-200 text-green-900',
  red: 'bg-red-50 border-red-200 text-red-900',
  purple: 'bg-purple-50 border-purple-200 text-purple-900',
  orange: 'bg-orange-50 border-orange-200 text-orange-900',
};

const iconColorClasses = {
  gold: 'text-amber-600',
  blue: 'text-blue-600',
  green: 'text-green-600',
  red: 'text-red-600',
  purple: 'text-purple-600',
  orange: 'text-orange-600',
};

export function StatCardWidget({
  title,
  value,
  subtitle,
  trend,
  icon,
  color = 'gold',
  onClick,
  loading = false,
}: StatCardWidgetProps) {
  return (
    <div
      onClick={onClick}
      className={`
        relative overflow-hidden rounded-lg border p-4 sm:p-6
        transition-all duration-200 ease-out
        ${colorClasses[color]}
        ${onClick ? 'cursor-pointer hover:shadow-md hover:scale-105' : ''}
        ${loading ? 'opacity-50' : ''}
      `}
    >
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-3">
          <p className="text-xs sm:text-sm font-medium opacity-75 mb-1">{title}</p>
          {icon && (
            <div className={`${iconColorClasses[color]} ml-2 flex-shrink-0`}>
              {icon}
            </div>
          )}
        </div>

        <div className="mb-3">
          {loading ? (
            <div className="h-8 sm:h-10 bg-current opacity-20 rounded animate-pulse w-24" />
          ) : (
            <p className="text-2xl sm:text-3xl font-bold tracking-tight">{value}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          {subtitle && (
            <p className="text-xs sm:text-sm opacity-70">{subtitle}</p>
          )}

          {trend && (
            <div className={`flex items-center gap-1 text-xs sm:text-sm font-medium ${
              trend.isPositive ? 'text-green-600' : 'text-red-600'
            }`}>
              {trend.isPositive ? (
                <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
              ) : (
                <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />
              )}
              <span>{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>
      </div>

      {onClick && (
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowRight className="w-4 h-4" />
        </div>
      )}
    </div>
  );
}
